import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";

const MARGIN = 8;

export default function HelpTooltip({ text, formula, darkMode }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const tooltipRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return;

    const computePosition = () => {
      const btn = buttonRef.current;
      const tip = tooltipRef.current;
      if (!btn) return null;

      const b = btn.getBoundingClientRect();
      const tipW = tip?.offsetWidth || 256;
      const tipH = tip?.offsetHeight || 0;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Default: sopra il bottone, centrato orizzontalmente
      let top = b.top - MARGIN - tipH;
      let left = b.left + b.width / 2 - tipW / 2;

      // Clamp dentro la viewport con margine di sicurezza
      left = Math.max(MARGIN, Math.min(left, vw - tipW - MARGIN));
      top = Math.max(MARGIN, Math.min(top, vh - tipH - MARGIN));

      // Posizione freccia: centro bottone relativo al tooltip
      const btnCenterX = b.left + b.width / 2;
      const arrowLeft = btnCenterX - left;

      return { top, left, arrowLeft };
    };

    const reposition = () => setPosition(computePosition());
    reposition();

    // Chiudi al primo scroll (pattern standard: macOS, Radix, Headless UI).
    // Il riposizionamento live è fragile sui containing block annidati.
    const handleScroll = () => setOpen(false);

    const handleClickOutside = (e) => {
      if (
        !buttonRef.current?.contains(e.target) &&
        !tooltipRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    window.addEventListener("resize", reposition);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("resize", reposition);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Reset della posizione alla chiusura per evitare flash alla riapertura
  useEffect(() => {
    if (!open) setPosition(null);
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`inline-flex items-center justify-center rounded-full transition-opacity ${
          darkMode
            ? "text-neutral-500 hover:text-neutral-300"
            : "text-neutral-400 hover:text-neutral-600"
        }`}
        aria-label="Informazioni"
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>

      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: "fixed",
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? "visible" : "hidden",
            }}
            className={`z-50 w-64 rounded-lg px-3 py-2 text-xs shadow-lg ${
              darkMode
                ? "bg-neutral-100 text-neutral-900"
                : "bg-neutral-900 text-white"
            }`}
          >
            <p>{text}</p>
            {formula && (
              <p
                className={`mt-1.5 font-mono text-[11px] ${
                  darkMode ? "text-neutral-600" : "text-neutral-400"
                }`}
              >
                {formula}
              </p>
            )}
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: position?.arrowLeft ?? 0,
                transform: "translateX(-50%)",
              }}
              className={`w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] ${
                darkMode ? "border-t-neutral-100" : "border-t-neutral-900"
              }`}
            />
          </div>,
          document.body
        )}
    </>
  );
}
